import { useEffect, useState } from 'react'
import { Upload, ListChecks, MessageSquare } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'

const STAGES = ['not_started', 'in_progress', 'review', 'filed']
const STAGE_LABELS = { not_started: 'Not Started', in_progress: 'In Progress', review: 'In Review', filed: 'Filed' }

export default function ClientDashboard() {
  const { profile } = useAuth()
  const navigate = useNavigate()
  const [clientData, setClientData] = useState(null)
  const [documents, setDocuments] = useState([])
  const [messages, setMessages] = useState([])
  const [invoices, setInvoices] = useState([])
  const [organizer, setOrganizer] = useState(null)
  const [pendingEngagement, setPendingEngagement] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function load() {
      if (!profile) return
      const { data: client } = await supabase
        .from('clients')
        .select('*')
        .eq('portal_user_id', profile.id)
        .maybeSingle()

      if (!client) { setLoading(false); return }
      setClientData(client)

      const [{ data: docs }, { data: msgs }, { data: invs }, { data: org }, { data: pendingLetter }] = await Promise.all([
        supabase.from('documents').select('*').eq('client_id', client.id).order('created_at', { ascending: false }),
        supabase.from('messages').select('*').eq('client_id', client.id).order('created_at', { ascending: false }).limit(5),
        supabase.from('invoices').select('*').eq('client_id', client.id).order('created_at', { ascending: false }),
        supabase.from('organizers').select('*').eq('client_id', client.id).maybeSingle(),
        supabase
          .from('engagement_letters')
          .select('id, title, status')
          .eq('client_id', client.id)
          .eq('status', 'sent')
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle(),
      ])
      setDocuments(docs || [])
      setMessages(msgs || [])
      setInvoices(invs || [])
      setOrganizer(org)
      setPendingEngagement(pendingLetter || null)
      setLoading(false)
    }
    load()
  }, [profile])

  if (loading) return (
    <div style={{ display: 'grid', gap: 16 }}>
      {[...Array(3)].map((_, i) => <div key={i} className="skeleton" style={{ height: 80 }} />)}
    </div>
  )

  if (!clientData) return (
    <div className="card animate-fadeIn" style={{ padding: '48px', textAlign: 'center', maxWidth: 480, margin: '60px auto' }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>👋</div>
      <h2 style={{ marginBottom: 8 }}>Welcome to ClearFile</h2>
      <p style={{ color: '#62615D', fontSize: 14, lineHeight: 1.6 }}>
        Your accountant hasn't linked your account yet. Please contact your tax professional and ask them to add you to the portal.
      </p>
    </div>
  )

  const currentStageIdx = STAGES.indexOf(clientData.status)
  const pendingDocs = documents.filter(d => d.status === 'needs_resubmission')
  const unpaidInvoices = invoices.filter(i => i.status === 'sent' || i.status === 'overdue')

  return (
    <div className="stagger client-dashboard-root">
      <style>{`
        @media (max-width: 768px) {
          .client-dashboard-root .client-welcome-row {
            flex-direction: column;
            align-items: flex-start !important;
            gap: 12px;
          }
          .client-dashboard-root .client-stage-scroll {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }
          .client-dashboard-root .client-stage-inner {
            min-width: 540px;
          }
          .client-dashboard-root .client-two-col {
            grid-template-columns: 1fr !important;
          }
          .client-dashboard-root .client-full-width {
            grid-column: auto !important;
          }
        }
      `}</style>
      {/* Welcome */}
      <div className="card animate-fadeUp" style={{
        padding: '24px 28px', marginBottom: 20,
        background: '#202326',
        border: 'none', color: '#fff'
      }}>
        <div className="client-welcome-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <p style={{ color: '#8892a4', fontSize: 12, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 1 }}>Welcome back</p>
            <h2 style={{ color: '#fff', fontSize: 22, marginBottom: 4 }}>{profile?.full_name}</h2>
            <p style={{ color: '#CBB17B', fontSize: 13 }}>{clientData.business_name} · Tax Year {clientData.tax_year}</p>
          </div>
          {clientData.deadline && (
            <div style={{ textAlign: 'right' }}>
              <div style={{ color: '#8892a4', fontSize: 11, marginBottom: 2 }}>Filing Deadline</div>
              <div style={{ color: '#CBB17B', fontWeight: 700, fontSize: 16 }}>
                {new Date(clientData.deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </div>
            </div>
          )}
        </div>
      </div>

      <nav className="cf-client-actions" aria-label="Your next steps">
        <Link to="/my-documents"><Upload size={25}/><div><strong>Upload documents</strong><span>Share files with your firm</span></div></Link>
        <Link to="/organizer"><ListChecks size={25}/><div><strong>Complete your organizer</strong><span>Answer your firm’s questions</span></div></Link>
        <Link to="/my-messages"><MessageSquare size={25}/><div><strong>Message your firm</strong><span>Keep the conversation together</span></div></Link>
      </nav>
      {pendingEngagement && (
        <div className="card animate-fadeUp" style={{
          padding: '18px 20px', marginBottom: 20,
          background: '#F1E8D7', border: '1px solid #D8C18F'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: 12, color: '#765B2C', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 4 }}>
                Engagement Letter Ready
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#202326', marginBottom: 2 }}>
                {clientData.full_name || clientData.business_name || profile?.full_name}
              </div>
              <div style={{ fontSize: 13, color: '#62615D' }}>
                {pendingEngagement.title || 'Tax Engagement Letter'}
              </div>
            </div>
            <button className="btn btn-primary" onClick={() => navigate('/engagement-signing')}>
              Review & Sign
            </button>
          </div>
        </div>
      )}

      {/* Filing status pipeline */}
      <div className="card animate-fadeUp" style={{ padding: '20px 24px', marginBottom: 20 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 16 }}>Your Filing Status</h3>
        <div className="client-stage-scroll">
        <div className="client-stage-inner" style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
          {STAGES.map((stage, idx) => {
            const done = idx < currentStageIdx
            const active = idx === currentStageIdx
            return (
              <div key={stage} style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                <div style={{ textAlign: 'center', flex: 1 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: '50%', margin: '0 auto 8px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 700, fontSize: 13, transition: 'all 0.3s',
                    background: done ? '#065F46' : active ? '#CBB17B' : '#F7F3EB',
                    color: done ? '#fff' : active ? '#202326' : '#62615D',
                    border: active ? '3px solid #765B2C' : '2px solid transparent',
                    boxShadow: active ? '0 0 0 4px rgba(201,168,76,0.2)' : 'none'
                  }}>
                    {done ? '✓' : idx + 1}
                  </div>
                  <div style={{ fontSize: 11, fontWeight: active ? 700 : 400, color: active ? '#202326' : '#62615D' }}>
                    {STAGE_LABELS[stage]}
                  </div>
                </div>
                {idx < STAGES.length - 1 && (
                  <div style={{ height: 2, width: 24, background: idx < currentStageIdx ? '#065F46' : '#DED6CA', flexShrink: 0, marginBottom: 24 }} />
                )}
              </div>
            )
          })}
        </div>
        </div>
      </div>

      {/* Alerts */}
      {(pendingDocs.length > 0 || unpaidInvoices.length > 0) && (
        <div style={{ display: 'grid', gap: 10, marginBottom: 20 }}>
          {pendingDocs.map(d => (
            <div key={d.id} style={{ background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 18 }}>⚠️</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>Document needs resubmission: {d.file_name}</div>
                {d.reviewer_note && <div style={{ fontSize: 12, color: '#62615D', marginTop: 2 }}>{d.reviewer_note}</div>}
              </div>
              <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={() => navigate('/my-documents')}>Upload</button>
            </div>
          ))}
          {unpaidInvoices.map(inv => (
            <div key={inv.id} style={{ background: '#F1E8D7', border: '1px solid #CBB17B', borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 18 }}>🧾</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>Invoice #{inv.invoice_number} — ${Number(inv.total_amount || 0).toFixed(2)}</div>
                <div style={{ fontSize: 12, color: '#62615D' }}>Due {inv.due_date ? new Date(inv.due_date).toLocaleDateString() : 'ASAP'}</div>
              </div>
              <button className="btn btn-primary" style={{ fontSize: 12 }} onClick={() => navigate('/my-invoices')}>Pay Now</button>
            </div>
          ))}
        </div>
      )}

      <div className="client-two-col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* Documents */}
        <div className="card animate-fadeUp">
          <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between' }}>
            <h3 style={{ fontSize: 14, fontWeight: 700 }}>My Documents</h3>
            <button className="btn btn-ghost" style={{ padding: '5px 12px', fontSize: 12 }} onClick={() => navigate('/my-documents')}>View All →</button>
          </div>
          {documents.slice(0, 4).map(d => (
            <div key={d.id} style={{ padding: '10px 18px', borderBottom: '1px solid #F7F3EB', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span>{d.file_name?.endsWith('.pdf') ? '📄' : '📊'}</span>
                <div style={{ fontSize: 12, fontWeight: 500 }}>{d.file_name}</div>
              </div>
              <span className={`badge badge-${d.status === 'reviewed' ? 'success' : d.status === 'needs_resubmission' ? 'danger' : 'warning'}`} style={{ fontSize: 10 }}>
                {d.status}
              </span>
            </div>
          ))}
          {documents.length === 0 && (
            <div style={{ padding: '20px', textAlign: 'center' }}>
              <button className="btn btn-primary" onClick={() => navigate('/my-documents')}>Upload Documents</button>
            </div>
          )}
        </div>

        {/* Tax Organizer */}
        <div className="card animate-fadeUp" style={{ padding: '18px' }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 6 }}>Tax Organizer</h3>
          <p style={{ fontSize: 12, color: '#62615D', marginBottom: 16 }}>Answer questions so your accountant has everything they need.</p>
          <div style={{
            background: organizer?.status === 'completed' ? '#D1FAE5' : '#F1E8D7',
            borderRadius: 8, padding: '14px 16px', marginBottom: 14, textAlign: 'center'
          }}>
            <div style={{ fontSize: 28, marginBottom: 4 }}>
              {organizer?.status === 'completed' ? '✅' : organizer?.status === 'in_progress' ? '📝' : '📋'}
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: organizer?.status === 'completed' ? '#065F46' : '#765B2C' }}>
              {organizer?.status === 'completed' ? 'Completed!' : organizer?.status === 'in_progress' ? 'In Progress' : 'Not Started'}
            </div>
          </div>
          <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }} onClick={() => navigate('/organizer')}>
            {organizer?.status === 'completed' ? 'Review Answers' : organizer?.status === 'in_progress' ? 'Continue Organizer' : 'Start Organizer'}
          </button>
        </div>

        {/* Recent messages */}
        <div className="card animate-fadeUp client-full-width" style={{ gridColumn: '1 / -1' }}>
          <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between' }}>
            <h3 style={{ fontSize: 14, fontWeight: 700 }}>Messages with your firm</h3>
            <button className="btn btn-ghost" style={{ padding: '5px 12px', fontSize: 12 }} onClick={() => navigate('/my-messages')}>View All →</button>
          </div>
          {messages.length === 0
            ? <div style={{ padding: '20px', textAlign: 'center', color: '#62615D', fontSize: 13 }}>No messages yet</div>
            : messages.map(m => (
            <div key={m.id} style={{ padding: '12px 18px', borderBottom: '1px solid #F7F3EB' }}>
              <div style={{ fontSize: 13, color: '#202326', marginBottom: 4 }}>{m.body}</div>
              <div style={{ fontSize: 11, color: '#62615D' }}>{new Date(m.created_at).toLocaleDateString()}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
